/**
 * 도메인별 tenantId 매핑 유틸리티 (백엔드 API 기반)
 */

import fastapi from '@/lib/api';

// 세션 기반 캐시 설정 (브라우저 세션 동안 유지)
let cachedDomainMappings = null;
let sessionCacheKey = 'domain_mappings_cache';
const HERO_DOMAIN = 'hero-assetcare.inoberry.co.kr';
const HERO_TENANT_ID = 2;

/**
 * 백엔드에서 도메인 매핑 정보를 가져옵니다.
 */
async function fetchDomainMappings() {
  try {
    // 세션 스토리지에서 캐시된 데이터 확인
    const sessionCache = sessionStorage.getItem(sessionCacheKey);
    if (sessionCache) {
      try {
        cachedDomainMappings = JSON.parse(sessionCache);
        return cachedDomainMappings;
      } catch (e) {
        // 캐시 파싱 실패 시 제거
        sessionStorage.removeItem(sessionCacheKey);
      }
    }
    
    // 메모리 캐시 확인
    if (cachedDomainMappings) {
      return cachedDomainMappings;
    }

    // 현재 도메인을 그대로 사용하여 백엔드에 전달 (VITE_API_SERVER_URL 사용하지 않음)
    const currentDomain = window.location.hostname;
    const protocol = window.location.protocol;
    const port = window.location.port ? `:${window.location.port}` : '';
    const baseUrl = `${protocol}//${currentDomain}${port}`;
    
    const response = await fetch(`${baseUrl}/api/v1/tenant-info/mappings`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        'X-Client-Domain': currentDomain
      }
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const responseData = await response.json();

    if (responseData && responseData.data) {
      // 특정 도메인(hybrid hero) 강제 매핑
      if (currentDomain === HERO_DOMAIN) {
        responseData.data.mapped_tenant_id = HERO_TENANT_ID;
        responseData.data.is_valid_domain = true;
        responseData.data.current_domain = HERO_DOMAIN;
        responseData.data.company_name =
          responseData.data.company_name || 'hero';
      }
      // 세션 스토리지와 메모리에 캐시 설정
      cachedDomainMappings = responseData.data;
      sessionStorage.setItem(sessionCacheKey, JSON.stringify(responseData.data));
      
      return cachedDomainMappings;
    } else {
      throw new Error('도메인 매핑 정보를 가져올 수 없습니다.');
    }
  } catch (error) {
    console.error('도메인 매핑 정보를 가져오는데 실패했습니다:', error);
    return null;
  }
}

/**
 * 현재 도메인에서 tenant ID를 가져옵니다.
 * 백엔드에서 도메인 기반으로 판단하여 반환
 * @returns {number|null} tenant ID 또는 null
 */
export async function getTenantIdFromDomain() {
  try {
    const domainInfo = await fetchDomainMappings();
    if (!domainInfo) return null;
    
    return domainInfo.mapped_tenant_id || null;
  } catch (error) {
    console.error('도메인에서 tenant ID를 가져오는데 실패했습니다:', error);
    return null;
  }
}

/**
 * 현재 도메인에서 회사명을 가져옵니다.
 * 백엔드에서 도메인 기반으로 판단하여 반환
 * @returns {string|null} 회사명 또는 null
 */
export async function getCompanyNameFromDomain() {
  try {
    const domainInfo = await fetchDomainMappings();
    if (!domainInfo) return null;
    
    return domainInfo.company_name || null;
  } catch (error) {
    console.error('도메인에서 회사명을 가져오는데 실패했습니다:', error);
    return null;
  }
}

/**
 * URL 경로에서 tenant ID를 추출합니다.
 * 로그인 페이지 등 인증이 필요 없는 페이지에서는 추출하지 않습니다.
 * @param {string} pathname - URL 경로 (예: "/2/dashboard" 또는 "/dashboard")
 * @returns {number|null} tenant ID 또는 null
 */
export function getTenantIdFromUrlPath(pathname) {
  if (!pathname || pathname === '/') {
    return null;
  }
  
  // 인증이 필요 없는 페이지들은 제외
  const publicPaths = ['/auth/login', '/auth/register', '/auth/forgot-password'];
  if (publicPaths.some(path => pathname.startsWith(path))) {
    return null;
  }
  
  // 경로의 첫 번째 세그먼트가 숫자인지 확인
  const pathParts = pathname.split('/').filter(part => part.length > 0);
  if (pathParts.length > 0 && /^\d+$/.test(pathParts[0])) {
    return parseInt(pathParts[0]);
  }
  
  return null;
}

/**
 * URL에서 tenant ID를 추출합니다.
 * @param {string} url - URL
 * @returns {number|null} tenant ID 또는 null
 */
export function getTenantIdFromUrl(url) {
  if (!url) return null;
  
  try {
    const urlObj = new URL(url);
    return getTenantIdFromUrlPath(urlObj.pathname);
  } catch (error) {
    console.error('URL에서 tenant ID를 추출하는데 실패했습니다:', error);
    return null;
  }
}

/**
 * URL에서 도메인을 추출합니다.
 * @param {string} url - URL
 * @returns {string|null} 도메인 또는 null
 */
export function getDomainFromUrl(url) {
  if (!url) return null;
  
  try {
    const urlObj = new URL(url);
    return urlObj.hostname;
  } catch (error) {
    console.error('URL에서 도메인을 추출하는데 실패했습니다:', error);
    return null;
  }
}

/**
 * 현재 페이지의 도메인 정보를 가져옵니다.
 * @returns {Object|null} 도메인 정보 또는 null
 */
export async function getCurrentDomainInfo() {
  try {
    const domainInfo = await fetchDomainMappings();
    if (!domainInfo) return null;
    
    return {
      domain: domainInfo.current_domain,
      tenant_id: domainInfo.mapped_tenant_id,
      company_name: domainInfo.company_name
    };
  } catch (error) {
    console.error('현재 도메인 정보를 가져오는데 실패했습니다:', error);
    return null;
  }
}

/**
 * 도메인 매핑 정보를 가져옵니다.
 * @returns {Object|null} 도메인 매핑 정보 또는 null
 */
export async function getDomainMappingInfo() {
  try {
    const domainInfo = await fetchDomainMappings();
    return domainInfo;
  } catch (error) {
    console.error('도메인 매핑 정보를 가져오는데 실패했습니다:', error);
    return null;
  }
}

/**
 * 특정 도메인이 고정 테넌트 도메인인지 확인합니다.
 * @returns {boolean} 고정 테넌트 도메인 여부
 */
export async function isFixedTenantDomain() {
  try {
    const domainInfo = await fetchDomainMappings();
    if (!domainInfo) return false;
    
    // 백엔드에서 판단한 결과 반환
    return domainInfo.is_valid_domain || false;
  } catch (error) {
    console.error('도메인 고정 테넌트 여부를 확인하는데 실패했습니다:', error);
    return false;
  }
}

/**
 * hero 도메인 여부 확인
 * @returns {boolean}
 */
export function isHeroDomain() {
  return window.location.hostname === HERO_DOMAIN;
}

/**
 * hero 도메인 전용 로고 경로
 * @returns {string}
 */
export function getHeroLogoPath() {
  return '/layout/images/logo/logo-hero.png';
}

/**
 * URL 오버라이드에서 tenant ID를 가져옵니다.
 * @param {string} url - URL
 * @returns {number|null} tenant ID 또는 null
 */
export function getTenantIdFromDomainOverrideUrl(url) {
  return getTenantIdFromUrl(url);
}

/**
 * 도메인 기반으로 자동으로 tenantId를 설정합니다.
 * 백엔드에서 도메인 기반으로 판단하여 설정
 * @returns {Promise<string|null>} 설정된 tenantId 또는 null
 */
export async function autoSetTenantIdFromDomain() {
  try {
    // 현재 도메인이 지정된 도메인인지 확인
    const isFixedDomain = await isFixedTenantDomain();
    
    // 지정되지 않은 도메인이면서 로컬스토리지에 tenantId가 있으면 그 값 사용
    if (!isFixedDomain) {
      const storedTenantId = localStorage.getItem('tenantId');
      if (storedTenantId) {
        return storedTenantId;
      }
    }

    const tenantId = await getTenantIdFromDomain();
    
    if (tenantId) {
      // localStorage에 저장
      localStorage.setItem('tenantId', tenantId.toString());
      localStorage.setItem('autoSetTenantId', 'true');
      
      return tenantId.toString();
    }
    
    return null;
  } catch (error) {
    console.error('도메인 기반 tenantId 자동 설정 실패:', error);
    return null;
  }
}

/**
 * 자동 설정된 tenantId인지 확인합니다.
 * @returns {boolean}
 */
export function isAutoSetTenantId() {
  return localStorage.getItem('autoSetTenantId') === 'true';
}

/**
 * 도메인 매핑 캐시를 초기화합니다.
 * 새로운 세션을 시작하거나 캐시를 리셋할 때 사용
 */
export function clearDomainMappingCache() {
  cachedDomainMappings = null;
  sessionStorage.removeItem(sessionCacheKey);
}

/**
 * 현재 도메인 매핑 캐시 상태를 확인합니다.
 * @returns {Object} 캐시 상태 정보
 */
export function getDomainMappingCacheStatus() {
  const hasMemoryCache = !!cachedDomainMappings;
  const hasSessionCache = !!sessionStorage.getItem(sessionCacheKey);
  
  return {
    hasMemoryCache,
    hasSessionCache,
    memoryCacheData: cachedDomainMappings,
    sessionCacheData: sessionStorage.getItem(sessionCacheKey) ? JSON.parse(sessionStorage.getItem(sessionCacheKey)) : null
  };
}
